import classNames from 'classnames';
import { FC, memo, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { isDesktop } from 'react-device-detect';
import { setBodyOverflow } from 'src/helpers/utils';
import { useAutoplayInView } from 'src/hooks/useAutoplayInView';
import { CmsRelation, CmsRelationItem } from 'src/types/cms/cmsFieldTypes';
import { CmsStoryGroup } from 'src/types/cms/cmsWidgetTypes';
import SwiperCore from 'swiper';
import 'swiper/css';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import './Stories.scss';
import { StoriesModal } from './StoriesModal';

type CmsExtendedRelationItem = CmsRelationItem<CmsStoryGroup> & { isViewed?: boolean };

const checkIfGroupIsViewed = (groupTitle: string): boolean => {
  let viewStoryGroups: any = localStorage.getItem('viewStoryGroups');

  if (viewStoryGroups) {
    viewStoryGroups = JSON.parse(viewStoryGroups);
    return viewStoryGroups.includes(groupTitle);
  }

  return false;
};

const mapGroupsWithViewedState = (groups?: CmsRelation<CmsStoryGroup> | null): CmsExtendedRelationItem[] =>
  groups
    ?.filter((group) => group.relation !== null)
    ?.map((group) => ({
      ...group,
      isViewed: checkIfGroupIsViewed(group.relation!.title),
    }))
    ?.sort((a, b) => {
      if (a.isViewed !== b.isViewed) return Number(!!a.isViewed) - Number(!!b.isViewed);
      return Number(!!b.relation?.label?.trim()) - Number(!!a.relation?.label?.trim());
    }) || [];

const persistViewedGroup = (groupTitle: string): void => {
  const stored = localStorage.getItem('viewStoryGroups');
  const viewStoryGroups: string[] = stored ? JSON.parse(stored) : [];
  viewStoryGroups.push(groupTitle);
  localStorage.setItem('viewStoryGroups', JSON.stringify(viewStoryGroups));
};

type StoriesSliderProps = {
  groups: CmsExtendedRelationItem[];
  onSelect: (group: CmsExtendedRelationItem) => void;
  swiperRef: MutableRefObject<SwiperCore | undefined>;
  overlayOpenRef: MutableRefObject<boolean>;
};

const areGroupsEqual = (a: CmsExtendedRelationItem[], b: CmsExtendedRelationItem[]): boolean =>
  a.length === b.length && a.every((g, i) => g?.value === b[i]?.value && g?.isViewed === b[i]?.isViewed);

// Content-memoized: re-render only when the story list changes, else the loop shuffles.
const StoriesSlider = memo(
  ({ groups, onSelect, swiperRef, overlayOpenRef }: StoriesSliderProps) => {
    const slidesPerView = isDesktop ? 12.5 : 3.5;
    const enableLoop = isDesktop && groups.length >= Math.ceil(slidesPerView) + 1;

    // pause autoplay out of view; never resume a static row or while the modal covers it
    const listRef = useAutoplayInView<HTMLDivElement>(swiperRef, () => enableLoop && !overlayOpenRef.current);

    // viewing a group re-sorts the list; loop mode reorders slide DOM, so remount to re-render the new order
    const orderKey = groups.map((g) => g?.value).join(',');

    const startAutoplay = (): void => {
      // don't resume a static row, or while the modal is open (it covers the cursor and fires mouseleave)
      if (swiperRef.current && enableLoop && !overlayOpenRef.current) {
        requestAnimationFrame(() => swiperRef.current?.autoplay?.start());
      }
    };

    const stopAutoplay = (): void => {
      if (swiperRef.current) {
        requestAnimationFrame(() => swiperRef.current?.autoplay?.stop());
      }
    };

    return (
      <div className="StoriesList" ref={listRef} onMouseEnter={stopAutoplay} onMouseLeave={startAutoplay}>
        <Swiper
          key={orderKey}
          onSwiper={(swiper: SwiperCore) => {
            swiperRef.current = swiper;
            // a remount (order change) can happen while the modal is open; keep the marquee frozen
            if (overlayOpenRef.current) {
              requestAnimationFrame(() => swiper.autoplay?.stop());
            }
          }}
          modules={[Autoplay]}
          slidesPerView={slidesPerView}
          slidesPerGroup={1}
          spaceBetween={10}
          speed={3000}
          loop={enableLoop}
          autoplay={
            enableLoop
              ? {
                  delay: 0,
                  disableOnInteraction: false,
                }
              : false
          }
        >
          {groups.map((group) => (
            <SwiperSlide key={group?.value}>
              <button onClick={() => onSelect(group)} className="StoriesItemWrapper">
                <div
                  className={classNames('StoriesItem', {
                    StoriesItemViewed: group?.isViewed,
                  })}
                >
                  <img className="StoriesItemImage" src={group.relation?.groupImage?.path} alt="" loading="lazy" />
                  {group.relation?.label && <div className="StoriesItemLabel">{group.relation?.label}</div>}
                </div>
                <span className="StoriesItemName">{group.relation?.title}</span>
              </button>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  },
  (prev, next) =>
    prev.onSelect === next.onSelect && prev.swiperRef === next.swiperRef && areGroupsEqual(prev.groups, next.groups)
);

StoriesSlider.displayName = 'StoriesSlider';

const StoriesWidget: FC<{ storiesData?: CmsRelation<CmsStoryGroup> | null }> = ({ storiesData }) => {
  const swiperRef = useRef<SwiperCore>();
  const overlayOpenRef = useRef<boolean>(false);
  const [storiesGroups, setStoriesGroups] = useState<CmsExtendedRelationItem[] | null>(null);
  const [group, setGroup] = useState<CmsExtendedRelationItem | null>(null);

  const handleSetGroup = useCallback((group: CmsExtendedRelationItem): void => {
    setGroup(group);
    setBodyOverflow('unset');
    // freeze the marquee while the modal is open so the row stays put on close
    overlayOpenRef.current = true;
    swiperRef.current?.autoplay?.stop();
  }, []);

  const handleClose = (): void => {
    setBodyOverflow('set');
    setGroup(null);
    overlayOpenRef.current = false;
    // only resume a looping row: Swiper's autoplay.start() ignores the disabled flag and would crawl a static row
    if (swiperRef.current?.params?.loop) swiperRef.current.autoplay?.start();
  };

  const handleAllStoriesEnd = (groupTitle?: string): void => {
    if (!groupTitle || checkIfGroupIsViewed(groupTitle)) return;

    persistViewedGroup(groupTitle);
    setStoriesGroups(mapGroupsWithViewedState(storiesGroups));
  };

  useEffect(() => {
    setStoriesGroups(mapGroupsWithViewedState(storiesData));
  }, [storiesData]);

  return storiesData && storiesData.length ? (
    <div className="Stories">
      <StoriesSlider
        groups={storiesGroups || []}
        onSelect={handleSetGroup}
        swiperRef={swiperRef}
        overlayOpenRef={overlayOpenRef}
      />

      <StoriesModal
        groupTitle={group?.relation?.title}
        open={!!group}
        onClose={handleClose}
        onAllStoriesEnd={handleAllStoriesEnd}
        stories={group?.relation?.stories || []}
      />
    </div>
  ) : null;
};

export default StoriesWidget;
